import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { DashboardOnboardingBanner } from '../DashboardOnboardingBanner';

interface Props {
  firstName?: string | null;
  email?: string | null;
  // Fires when the user clicks any of the three CTAs (Continue setup,
  // Find an expert, Invite teammates). Caller is expected to open the
  // self-service signup modal — this banner is purely presentational and
  // does not own modal state or navigation.
  onContinueSetup: () => void;
}

interface ActionCardProps {
  icon: JSX.Element;
  title: string;
  body?: string;
  hint?: string;
  highlight?: boolean;
  onClick: () => void;
}

function ActionCard({ icon, title, body, hint, highlight, onClick }: ActionCardProps): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        boxSizing: 'border-box',
        width: '100%',
        // Stretch to match the tallest sibling so adjacent cards always
        // align vertically regardless of body/hint content differences.
        height: '100%',
        textAlign: 'left',
        bgcolor: 'background.paper',
        border: highlight ? '2px solid' : '1px solid',
        borderColor: highlight ? 'primary.main' : 'grey.200',
        borderRadius: 2,
        px: 3,
        py: 2.5,
        transition: 'border-color 120ms ease',
        '&:hover': { borderColor: 'primary.main' },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'var(--brandPrimary)' }}
      >
        {icon}
        <Typography
          variant="overline"
          sx={{ fontWeight: 600, letterSpacing: '0.08em', fontSize: 12 }}
        >
          ACTION
        </Typography>
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25, color: 'text.primary' }}>
        {title}
      </Typography>
      {body && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {body}
        </Typography>
      )}
      {hint && (
        <Typography
          variant="body2"
          sx={{ color: 'var(--brandPrimary)', fontWeight: 600, mt: 1.5, fontSize: 13 }}
        >
          → {hint}
        </Typography>
      )}
    </Box>
  );
}

export function SetupPausedBanner({ firstName, email, onContinueSetup }: Props): JSX.Element {
  return (
    <DashboardOnboardingBanner
      firstName={firstName}
      email={email}
      subheading={<>Your account is ready. Set up your team to start booking consultations.</>}
      chipLabel="Setup incomplete"
      title="Finish setting up your team to start booking experts"
      body={
        <>
          You&apos;ll need a team and an active subscription before exploring the network of
          experts.
        </>
      }
      action={
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowForwardIcon />}
          onClick={onContinueSetup}
          sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
        >
          Continue setup
        </Button>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ActionCard
            icon={<SearchIcon fontSize="small" />}
            title="Find an expert"
            body="Explore thousands of vetted SMEs"
            hint="Continue your setup to unlock"
            highlight
            onClick={onContinueSetup}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ActionCard
            icon={<GroupAddOutlinedIcon fontSize="small" />}
            title="Invite teammates"
            onClick={onContinueSetup}
          />
        </Grid>
      </Grid>
    </DashboardOnboardingBanner>
  );
}
